import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { GoalApi } from '../../services/api/GoalApi';
import BasicsStep from './steps/goal/BasicsStep';
import AttachmentsStep from './steps/goal/AttachmentsStep';
import FollowupsStep from './steps/goal/FollowupsStep';
import ReviewStep from './steps/goal/ReviewStep';
import { useAsyncStep } from './hooks/useAsyncStep';
import './CreateCourseWizard.css';

/**
 * CreateGoalWizard — self-directed goal/project plan creation flow.
 *
 * Linear four-step machine: basics → attachments → followups → review.
 * Each step lives in its own component under `steps/goal/`; this file owns
 * the form state, API orchestration, and overall layout.
 *
 * Attachments are optional: the user can upload documents (sports schedule,
 * syllabus, project brief, activity calendar, etc.) and AI-Service extracts
 * events / deadlines / blackouts / topics that constrain the generated plan.
 *
 * Wraps three API calls:
 *   POST /api/plans/intake-questions   (AI-Service)
 *   POST /api/plans/generate           (AI-Service)
 *   POST /api/endeavors/from-plan      (hibernate-service)
 */
function CreateGoalWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clearCache, hydrateDashboard } = useData() || {};
  const { loading, error, run } = useAsyncStep();

  const [step, setStep] = useState('basics');
  const [basics, setBasics] = useState({
    title: '',
    category: 'learning',
    targetDate: '',
    weeklyHours: 5,
    currentLevel: '',
    motivation: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [attachedDocuments, setAttachedDocuments] = useState([]); // [{id, name}]
  const [followupQuestions, setFollowupQuestions] = useState([]);
  const [followupAnswers, setFollowupAnswers] = useState({});
  const [plan, setPlan] = useState(null);
  const [documentContext, setDocumentContext] = useState(null);

  const basicsValid = useMemo(() => (
    basics.title.trim().length > 2 &&
    basics.targetDate &&
    Number(basics.weeklyHours) > 0
  ), [basics]);

  const updateBasic = useCallback((field) => (e) => {
    const v = e?.target ? e.target.value : e;
    setBasics((prev) => ({ ...prev, [field]: v }));
  }, []);

  const setFollowupAnswer = useCallback((id, value) => {
    setFollowupAnswers((prev) => ({ ...prev, [id]: value }));
  }, []);

  const addAttachment = useCallback((doc) => {
    setAttachedDocuments((prev) => (
      prev.some((d) => d.id === doc.id) ? prev : [...prev, doc]
    ));
  }, []);

  const removeAttachment = useCallback((id) => {
    setAttachedDocuments((prev) => prev.filter((d) => d.id !== id));
  }, []);

  // ── Step transitions ──────────────────────────────────

  const goToAttachments = useCallback(() => {
    if (!basicsValid) return;
    setStep('attachments');
  }, [basicsValid]);

  const goToFollowups = useCallback(() => {
    if (!basicsValid) return;
    run(async () => {
      const documentIds = attachedDocuments.map((d) => d.id);
      const resp = await GoalApi.getIntakeFollowups(basics, documentIds);
      if (!resp.ok) throw new Error(`Intake questions failed (${resp.status})`);
      const body = await resp.json();
      const qs = Array.isArray(body?.questions) ? body.questions : [];
      setFollowupQuestions(qs);
      setFollowupAnswers(qs.reduce((acc, q) => ({ ...acc, [q.id]: '' }), {}));
      if (body?.documentContext) setDocumentContext(body.documentContext);
      setStep('followups');
    }, 'Failed to load follow-up questions');
  }, [basics, basicsValid, attachedDocuments, run]);

  const goToReview = useCallback(() => {
    run(async () => {
      const answers = followupQuestions.map((q) => ({
        id: q.id,
        question: q.question,
        answer: followupAnswers[q.id] || '',
      }));
      const documentIds = attachedDocuments.map((d) => d.id);
      const resp = await GoalApi.generatePlan(basics, answers, documentIds);
      if (!resp.ok) throw new Error(`Plan generation failed (${resp.status})`);
      const generated = await resp.json();
      setPlan(generated);
      if (generated?.documentContext) setDocumentContext(generated.documentContext);
      setStep('review');
    }, 'Failed to generate plan');
  }, [basics, followupQuestions, followupAnswers, attachedDocuments, run]);

  const regeneratePlan = useCallback(() => {
    setPlan(null);
    goToReview();
  }, [goToReview]);

  const confirmPlan = useCallback(() => {
    if (!plan) return;
    run(async () => {
      // Strip transient meta and any non-schema keys before POST.
      const { _meta, documentContext: _ctx, ...rest } = plan;
      const payload = {
        ...rest,
        documentIds: attachedDocuments.map((d) => d.id),
      };
      const resp = await GoalApi.createFromPlan(payload);
      if (!resp.ok) throw new Error(`Persisting plan failed (${resp.status})`);
      const created = await resp.json();
      const newEndeavorId = created?.endeavor?.endeavorId;
      try { clearCache && clearCache(); } catch (_) { /* non-fatal */ }
      try { hydrateDashboard && await hydrateDashboard(user?.id); } catch (_) { /* non-fatal */ }
      navigate('/dashboard', {
        state: newEndeavorId ? { highlightCourseId: newEndeavorId } : undefined,
      });
    }, 'Failed to create plan');
  }, [plan, attachedDocuments, clearCache, hydrateDashboard, user, navigate, run]);

  // ── Layout ─────────────────────────────────────────────────────────

  return (
    <div className="create-course-wizard single-page">
      {loading && step !== 'basics' && (
        <div className="course-creation-overlay" role="alert" aria-live="polite">
          <div className="course-creation-panel">
            <div className="course-spinner" aria-hidden="true" />
            <p className="spinner-line">
              {step === 'attachments'
                ? 'Reading your documents…'
                : step === 'followups'
                  ? 'Drafting your plan…'
                  : 'Saving plan…'}
            </p>
          </div>
        </div>
      )}

      <header className="wizard-header">
        <div className="wizard-header-inner">
          <button
            type="button"
            className="wizard-back-link"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="wizard-page-title">Create Goal</h1>
          <div className="wizard-header-spacer" />
        </div>
      </header>

      <div className="wizard-content">
        {error && (
          <div className="goal-error" role="alert">
            {error}
          </div>
        )}

        {step === 'basics' && (
          <BasicsStep
            basics={basics}
            onChange={updateBasic}
            basicsValid={basicsValid}
            loading={loading}
            onCancel={() => navigate(-1)}
            onNext={goToAttachments}
          />
        )}

        {step === 'attachments' && (
          <AttachmentsStep
            documents={attachedDocuments}
            onAdd={addAttachment}
            onRemove={removeAttachment}
            loading={loading}
            onBack={() => setStep('basics')}
            onNext={goToFollowups}
          />
        )}

        {step === 'followups' && (
          <FollowupsStep
            questions={followupQuestions}
            answers={followupAnswers}
            onAnswer={setFollowupAnswer}
            loading={loading}
            onBack={() => setStep('attachments')}
            onNext={goToReview}
          />
        )}

        {step === 'review' && (
          <ReviewStep
            plan={plan}
            documentContext={documentContext}
            attachedDocuments={attachedDocuments}
            loading={loading}
            onBack={() => setStep('followups')}
            onRegenerate={regeneratePlan}
            onConfirm={confirmPlan}
          />
        )}
      </div>
    </div>
  );
}

export default CreateGoalWizard;
